import type { Lead, LeadStatus } from './types'

// Pure helpers for the New jobs screen — status rules, sorting, filtering,
// form parsing. No DOM here, so scripts/new-jobs-unit.test.mjs can test it
// headlessly. The status rule is the same one functions/lib/leads.ts applies
// server-side; the two must stay word-for-word identical or the badge and
// the stored status drift apart.

export function normalizeLeadStatus(raw: string | null | undefined): LeadStatus {
  const t = (raw ?? '').trim().toUpperCase()
  if (t === 'CONFIRMED') return 'confirmed'
  if (t === 'DECLINED') return 'declined'
  if (t === 'CANCELLED') return 'cancelled'
  if (t.startsWith('QUOTE')) return 'quote'
  return 'other'
}

// ---- sorting ---------------------------------------------------------------
// Same rules the grid sorter runs on: never mutate the input, null/blank is
// LAST in both directions ("no data" is not a value), ties break on id, dates
// chronological (ISO text), money/cycle numeric, text natural collation.

export type SortDir = 'asc' | 'desc'

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

function textValue(v: string | null | undefined): string | null {
  const t = (v ?? '').trim()
  return t === '' ? null : t
}

// Sort keys are the sheet's 12 column fields. 'status' sorts the DERIVED word
// (what the badge groups by), not the raw variants.
export const LEAD_SORT_KEYS = [
  'quoted_on',
  'name',
  'address',
  'phone',
  'initial_pence',
  'regular_pence',
  'cycle_weeks',
  'status',
  'initial_lead',
  'notes',
  'checked',
  'notes2',
] as const

export type LeadSortKey = (typeof LEAD_SORT_KEYS)[number]

function sortValue(l: Lead, key: string): string | number | null {
  switch (key) {
    case 'quoted_on':
      return textValue(l.quoted_on)
    case 'name':
      return textValue(l.name)
    case 'address':
      return textValue(l.address)
    case 'phone':
      return textValue(l.phone)
    case 'initial_pence':
      return l.initial_pence ?? null
    case 'regular_pence':
      return l.regular_pence ?? null
    case 'cycle_weeks':
      return l.cycle_weeks && l.cycle_weeks > 0 ? l.cycle_weeks : null
    case 'status':
      return l.status
    case 'initial_lead':
      return textValue(l.initial_lead)
    case 'notes':
      return textValue(l.notes)
    case 'checked':
      return l.checked ?? null
    case 'notes2':
      return textValue(l.notes2)
    default:
      return null
  }
}

export function compareValues(a: string | number | null, b: string | number | null, dir: SortDir): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const cmp =
    typeof a === 'number' && typeof b === 'number'
      ? a < b ? -1 : a > b ? 1 : 0
      : collator.compare(String(a), String(b))
  return dir === 'desc' ? -cmp : cmp
}

export function sortLeads(rows: Lead[], key: string, dir: SortDir): Lead[] {
  return [...rows].sort((a, b) => compareValues(sortValue(a, key), sortValue(b, key), dir) || a.id - b.id)
}

// ---- filtering / search -----------------------------------------------------

// The colour a row wears. The sheet's own key: green confirmed, yellow a
// quote in play, purple a maybe, red declined, grey cancelled. "Maybe" is a
// quote whose wording says so ("MAYBE", "Quote, MAYBE") — the stored status
// stays 'quote'; only the colour and the Maybe filter read the word.
export type LeadTone = LeadStatus | 'maybe'

export function leadTone(l: Pick<Lead, 'status' | 'status_raw'>): LeadTone {
  // A job marked as lost ("LOSS", "LOSS, CONFIRMED") did not go ahead: it sits with the declined ones,
  // never in "Needs sorting" (28 Sep 2026 review). Nothing stored changes.
  if (/\bloss\b/i.test(l.status_raw ?? '')) return 'declined'
  if (/\bmaybe\b/i.test(l.status_raw ?? '')) return 'maybe'
  return l.status
}

export interface LeadFilters {
  search: string
  status: '' | LeadTone // '' = all; 'maybe' = the purple ones
  checked: '' | 'yes' | 'no' // '' = all
}

function searchHaystack(l: Lead): string {
  // The sheet has two Notes columns; a search that saw only one would hide
  // rows the user knows exist.
  return [l.name, l.address, l.phone, l.notes, l.notes2].map((v) => (v ?? '').toLowerCase()).join('\n')
}

export function leadMatches(l: Lead, f: LeadFilters): boolean {
  const q = f.search.trim().toLowerCase()
  if (q && !searchHaystack(l).includes(q)) return false
  // By colour, the same grouping as the board's columns, so every job is in exactly one group and
  // the counts add up (a "Quote, MAYBE" is a Maybe, not also a Quote).
  if (f.status && leadTone(l) !== f.status) return false
  if (f.checked === 'yes' && !l.checked) return false
  if (f.checked === 'no' && l.checked) return false
  return true
}

// ---- status dropdown options ---------------------------------------------------

// The statuses offered for a job (28 Sep 2026: a review of the live page found 14 near-duplicate
// choices, "Quote" / "QUOTE", "MAYBE" / "Quote, MAYBE", "LOSS" / "LOSS, CONFIRMED" and so on, and a
// free-text Status box that kept making more). One word per column of the board, plus the two quote
// states Lewis uses. "Didn't go ahead" is DECLINED or CANCELLED; "LOSS" is no longer offered.
export const STATUS_CHOICES = [
  'Quote',
  'Quote, NOT HOME',
  'Quote, WAITING',
  'MAYBE',
  'CONFIRMED',
  'DECLINED',
  'CANCELLED',
] as const

// Options for one job's Status dropdown: the choices above, plus that job's OWN wording when it is
// something else (an imported "QUOTE" or "LOSS, CONFIRMED"), so opening it can never silently reset
// it. Other jobs' odd wordings are not offered: picking one is how the copies spread. Exact text,
// case kept.
export function statusOptions(current: string | null | undefined): string[] {
  const opts: string[] = [...STATUS_CHOICES]
  const t = (current ?? '').trim()
  if (t !== '' && !opts.includes(t)) opts.push(t)
  return opts
}

// How a stored status reads on screen (bug hunt 8 Oct 2026: the board said "Won" where the card and filter said
// "CONFIRMED" / "Confirmed"): the board's own column words, sentence case. Display only; the stored and imported
// wording is never changed, and wording outside the list shows exactly as stored.
const STATUS_WORDS: Record<string, string> = {
  Quote: 'Quoted',
  'Quote, NOT HOME': 'Quoted, not home',
  'Quote, WAITING': 'Quoted, waiting for them',
  MAYBE: 'Maybe later',
  CONFIRMED: 'Won',
  DECLINED: "Didn't go ahead: declined",
  CANCELLED: "Didn't go ahead: cancelled",
}
export function statusWords(raw: string | null | undefined): string {
  const t = (raw ?? '').trim()
  return t === '' ? 'New enquiry' : STATUS_WORDS[t] ?? t
}
// Which board column a status puts a job in, in the board's words ("Counts as: Won").
export function statusColumnWords(raw: string | null | undefined): string {
  const key = boardColumn({ status: normalizeLeadStatus(raw), status_raw: raw ?? null })
  return BOARD_COLUMNS.find((c) => c.key === key)?.label ?? 'Needs sorting'
}

// The value a row's Status dropdown shows: the exact raw text, or '' when the
// sheet never had one — the derived word alone must never display as if it
// were the stored record.
export function statusSelectValue(raw: string | null | undefined): string {
  const t = (raw ?? '').trim()
  return t === '' ? '' : t
}

// A background request or offline cache can predate a just-acknowledged edit.
// Never replace that acknowledged row with an older version on this screen.
export function newerLead(incoming: Lead, held?: Lead): Lead {
  return held && held.id === incoming.id && held.version > incoming.version ? held : incoming
}

// ---- form parsing ------------------------------------------------------------

// Cycle weeks: blank = none, otherwise a positive decimal number of weeks.
export function parseCycleWeeks(input: string): number | null {
  const t = input.trim().replace(/weeks?/i, '').trim()
  if (t === '') return null
  const n = Number(t)
  if (!Number.isFinite(n) || n <= 0) throw new Error('cycle as weeks, e.g. 8 or 2.5')
  if (n > 520) throw new Error('cycle weeks too large (max 520)')
  return n
}

// What the Status form field shows under it, so the raw text's meaning is
// spelled out before it is saved. Blank raw is a real input (a row with no
// status word yet) and previews as its derived word.
export function statusPreview(raw: string | null | undefined): string {
  // The same grouping the chips and the board use, so "MAYBE" reads maybe and a lost job declined.
  return leadTone({ status: normalizeLeadStatus(raw), status_raw: raw ?? null })
}

// Still an open enquiry: not won and not dead. Messages and the week panel's enquiry rows use it, so a
// job the board shows under "Didn't go ahead" (a "LOSS" wording included) is never chased (28 Sep 2026).
export function leadIsOpen(l: Pick<Lead, 'status' | 'status_raw'>): boolean {
  const t = leadTone(l)
  return t !== 'confirmed' && t !== 'declined' && t !== 'cancelled'
}

// ---- the board (redesign, Liam 2026-09-25; Astra's rule for odd statuses) ----
// Which column an enquiry sits in, from its stored wording (the same tones the
// chips and badges use). A blank status is a new enquiry; wording that fits no
// column ("NOT HOME", "LOSS", anything typed) waits in "Needs sorting", never
// hidden. Declined and cancelled fold away as "Didn't go ahead".
export type BoardColumn = 'new' | 'quoted' | 'maybe' | 'won' | 'sorting' | 'closed'

export const BOARD_COLUMNS: { key: BoardColumn; label: string; status: string | null }[] = [
  { key: 'new', label: 'New enquiry', status: '' },
  { key: 'quoted', label: 'Quoted', status: 'Quote' },
  { key: 'maybe', label: 'Maybe later', status: 'MAYBE' },
  { key: 'won', label: 'Won', status: 'CONFIRMED' },
  // No status to drop into: these are sorted by picking one on the card.
  { key: 'sorting', label: 'Needs sorting', status: null },
  { key: 'closed', label: "Didn't go ahead", status: null },
]

export function boardColumn(l: Pick<Lead, 'status' | 'status_raw'>): BoardColumn {
  const tone = leadTone(l)
  if (tone === 'confirmed') return 'won'
  if (tone === 'declined' || tone === 'cancelled') return 'closed'
  if (tone === 'maybe') return 'maybe'
  if (tone === 'quote') return 'quoted'
  return (l.status_raw ?? '').trim() ? 'sorting' : 'new'
}
